import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Square, Calendar, ArrowUpDown } from 'lucide-react';
import { getProjects } from '../data/api';

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [showBeforeAfter, setShowBeforeAfter] = useState({});
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    getProjects().then(setAllProjects).catch(() => {});
  }, []);

  const categories = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'commercial', label: 'Commercial' }
  ];

  const filteredProjects = filter === 'all'
    ? allProjects
    : allProjects.filter(p => p.category === filter);

  const toggleBeforeAfter = (projectId) => {
    setShowBeforeAfter(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  return (
    <section id="portfolio" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Z-Pattern: Top Left to Top Right - Header with Filter on opposite side */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
          {/* Left: Section Header */}
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-3">
              Our Portfolio
            </h2>
            <p className="text-lg text-gray-600">
              Explore our collection of beautifully designed spaces
            </p>
          </div>

          {/* Right: Filter Buttons */}
          <div className="flex flex-wrap gap-3">
            {categories.map(category => (
              <Button
                key={category.id}
                onClick={() => setFilter(category.id)}
                variant={filter === category.id ? 'default' : 'outline'}
                className={`transition-all duration-300 ${
                  filter === category.id
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
                    : 'hover:border-blue-600 hover:text-blue-600'
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
            >
              {/* Before/After Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={showBeforeAfter[project.id] ? project.beforeImage : project.afterImage}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Before/After Toggle */}
                <Button
                  onClick={() => toggleBeforeAfter(project.id)}
                  size="sm"
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-900 hover:bg-white shadow-lg"
                >
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  {showBeforeAfter[project.id] ? 'After' : 'Before'}
                </Button>

                <Badge className="absolute bottom-4 left-4 bg-blue-600 text-white">
                  {project.category}
                </Badge>
              </div>

              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {project.description}
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {project.location}
                  </div>
                  <div className="flex items-center gap-1">
                    <Square className="w-4 h-4" />
                    {project.area}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {project.year}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button
                    variant="ghost"
                    className="w-full text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                  >
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;